import type { MouseEventProps, UIComponent } from '@gik/core/types/UI';
import type { HorVerLayout } from '@gik/core/types/layouts';
import { LayoutTypes } from '@gik/core/types/layouts';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { PageAvatar } from '@gik/ui-legacy/Avatar';
import { HTMLParser } from '@gik/ui-legacy/HTMLParser';
import { KeepProportions } from '@gik/ui-legacy/KeepProportions';
import type React from 'react';

export type InkindPageCardBaseProps = {
  title: string;
  imageUrl: string;
  location?: string;
  layout?: HorVerLayout;
  admin?: boolean;
  toolbar?: React.ReactNode;
} & UIComponent &
  MouseEventProps;

const blockName = 'inkind-page-card-base';

export function InkindPageCardBase({
  layout,
  admin,
  toolbar,
  imageUrl,
  title,
  location,
  className,
  ...otherProps
}: InkindPageCardBaseProps): JSX.Element {
  const bem = useBemCN(blockName);

  return (
    <div {...bem(null, [{ admin }, { row: layout === LayoutTypes.horizontal }], className)} {...otherProps}>
      <KeepProportions proportion={1}>
        <PageAvatar {...bem('image')} imageUrl={imageUrl} circle={false} />
      </KeepProportions>

      <section {...bem('content')}>
        <section {...bem('title')}>
          {/* Pages with a blank/removed title come through as `undefined` (e.g.
              `InkindPageCard` passes the raw API `title` with no fallback) —
              guard so this never throws and trips the card's error boundary
              (surfacing as an unexplained red exclamation icon). */}
          <HTMLParser rawHtml={(title || '').length > 75 ? title.substr(0, 72) + '...' : title || ''} />
        </section>

        {/* Always render the location line — using a non-breaking space when a
            page has no location — so cards keep a consistent height in grids. */}
        <section {...bem('location')}>{location || <>&nbsp;</>}</section>
      </section>

      {toolbar}
    </div>
  );
}
